'use client';

import { formatReadableNumber } from '@lfg/core/lib';
import { Box, CircularProgress, Grid2, Skeleton, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

import { icons } from '@/assets/icons';
import { dividers, highlights } from '@/assets/materials';
import type { NodeSchemaType } from '@/global/schemas/node.schema';
import { useFetchNodesQuery } from '@/store/services/node.service';

import { CoinIcon } from './bases/CoinIcon';
import { FilterBar } from './bases/FilterBar';
import HexagonalButton from './bases/HexagonalButton';
import { NodeCard } from './bases/NodeCard';
import NodeDetailModal, { type NodeDetailModalHandle } from './bases/NodeDetailModal';
import { Pagination } from './bases/Pagination';

export const ExploreLiveNodeSection = () => {
  const modalRef = useRef<NodeDetailModalHandle>(null);
  const [page, setPage] = useState<number>(1);

  const [selectedNode, setSelectedNode] = useState<NodeSchemaType | null>(null);

  const [searchValue, setSearchValue] = useState<string>('');
  const [sortValue, setSortValue] = useState<string>('{"price": "desc"}');
  const [networkValues, setNetworkValues] = useState<string[]>([]);

  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const {
    data: nodes,
    isLoading: isLoadingNodes,
    isFetching: isFetchingNodes,
  } = useFetchNodesQuery({
    page,
    search: debouncedSearch || undefined,
    sort: sortValue,
    filter: JSON.stringify({
      networkId: networkValues.length > 0 ? { in: networkValues } : undefined,
    }),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortValue, networkValues]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);

    // Scroll to top of live nodes section
    const element = document.getElementById('explore-live-node');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const openNodeSaleUrl = (url: string) => {
    window.open(url, '_blank');
  };

  const handleOpenNodeDetail = (item: NodeSchemaType) => {
    setSelectedNode(item);
    setTimeout(() => modalRef.current?.show(), 300);
  };

  return (
    <Stack
      id={'explore-live-node'}
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      minHeight={'852px'}
      padding={{ xs: '32px 24px', md: '80px' }}
      marginY={{ xs: '12px', md: '30px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      {selectedNode !== null && (
        <NodeDetailModal ref={modalRef} item={selectedNode} setSelectedNode={setSelectedNode} />
      )}
      <Stack gap={{ xs: 2, sm: 7 }} width={'min(1280px, 100%)'} zIndex={2}>
        <Stack
          justifyContent={'space-between'}
          gap={2}
          width={'100%'}
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{
            alignItems: { xs: 'flex-start', md: 'center' },
          }}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)', whiteSpace: 'nowrap' }}>
            LIVE SALES
          </Typography>

          {/* Filter Bar */}
          <FilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            sortValue={sortValue}
            onSortChange={setSortValue}
            networkValues={networkValues}
            onNetworkChange={setNetworkValues}
          />
        </Stack>

        {/* Loading and Content States */}
        {isLoadingNodes ? (
          // Initial loading state with skeletons
          <Grid2 container width={'100%'} spacing={{ xs: 2, md: 4 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6 }}>
                <NodeCard className={'h-full'}>
                  <Stack overflow={'hidden'} sx={{ height: '100%' }} justifyContent={'space-between'}>
                    <Stack>
                      <Stack
                        paddingX={{ xs: 3, sm: 4 }}
                        pt={{ xs: 3, sm: 4 }}
                        height={{ xs: '220px', sm: '320px' }}
                        width={'100%'}
                        overflow={'hidden'}>
                        <Skeleton
                          variant='rectangular'
                          width='100%'
                          height='100%'
                          sx={{ borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                      </Stack>
                      <Stack paddingX={{ xs: 3, sm: 4 }} gap={{ xs: 1, sm: 2 }}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                          <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Skeleton
                              variant='circular'
                              width={24}
                              height={24}
                              sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                            />
                            <Skeleton
                              variant='text'
                              width={80}
                              height={20}
                              sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                            />
                          </Stack>
                          <Skeleton
                            variant='text'
                            width={60}
                            height={16}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>
                        <Stack gap={0.5}>
                          <Skeleton
                            variant='text'
                            width='90%'
                            height={24}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='100%'
                            height={20}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                          <Skeleton
                            variant='text'
                            width='80%'
                            height={20}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack>
                      <Stack px={{ xs: 3, sm: 5 }} sx={{ width: '100%' }} justifyContent={'center'}>
                        <Skeleton
                          variant='rectangular'
                          width='100%'
                          height={2}
                          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                      </Stack>
                      <Stack padding={{ xs: '24px', sm: '32px 40px' }} minHeight={{ xs: 72, sm: 116 }}>
                        <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} gap={2}>
                          <Stack alignItems={'center'} direction={'row'} gap={{ xs: '12px', sm: 2 }}>
                            <Skeleton
                              variant='circular'
                              width={52}
                              height={52}
                              sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                            />
                            <Skeleton
                              variant='text'
                              width={120}
                              height={36}
                              sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                            />
                          </Stack>
                          <Skeleton
                            variant='rectangular'
                            width={84}
                            height={40}
                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </NodeCard>
              </Grid2>
            ))}
          </Grid2>
        ) : nodes?.data?.length === 0 ? (
          // No nodes found state
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              minHeight: '400px',
              padding: '40px 20px',
              textAlign: 'center',
            }}>
            <Stack alignItems='center' gap={3}>
              <Stack gap={1}>
                <Typography
                  variant='h4'
                  sx={{
                    color: 'white',
                    fontSize: { xs: '20px', sm: '24px' },
                    fontWeight: 600,
                  }}>
                  No nodes found
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: { xs: '14px', sm: '16px' },
                    maxWidth: '400px',
                  }}>
                  {searchValue || networkValues.length > 0
                    ? 'Try adjusting your search or filter criteria to find more nodes.'
                    : 'There are currently no live nodes available. Check back later for new listings.'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          // Nodes grid with loading overlay for pagination/filter changes
          <Box sx={{ position: 'relative' }}>
            {isFetchingNodes && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '8px',
                }}>
                <Stack alignItems='center' gap={2}>
                  <CircularProgress
                    size={40}
                    sx={{
                      color: 'white',
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                    }}>
                    Loading nodes...
                  </Typography>
                </Stack>
              </Box>
            )}
            <Grid2 container width={'100%'} spacing={{ xs: 2, md: 4 }}>
              {nodes?.data?.map((item, index) => (
                <Grid2 key={`${item.symbol}_${index + 1}`} size={{ xs: 12, sm: 6 }}>
                  <NodeCard className={'h-full'} onClick={() => handleOpenNodeDetail(item)}>
                    <Stack overflow={'hidden'} sx={{ height: '100%' }} justifyContent={'space-between'}>
                      <Stack>
                        <Stack
                          paddingX={{ xs: 3, sm: 4 }}
                          pt={{ xs: 3, sm: 4 }}
                          height={{ xs: '220px', sm: '320px' }}
                          width={'100%'}
                          overflow={'hidden'}>
                          <Image
                            priority
                            src={item.asset}
                            alt={item.name}
                            width={576}
                            height={320}
                            className={'w-full object-contain h-[220px] sm:h-[320px]'}
                          />
                        </Stack>
                        <Stack
                          padding={{ xs: '16px 24px 40px', sm: '24px 40px 60px' }}
                          minHeight={{ xs: 300, sm: 364 }}>
                          <Stack gap={{ xs: 2, sm: 4 }}>
                            <Stack alignItems={'center'} gap={1} direction={'row'}>
                              <Image
                                src={item.networkImage ?? '/icons/coins/ethereum.svg'}
                                alt={'highlight'}
                                width={15}
                                height={24}
                                className={'w-[auto] h-[24px] object-contain'}
                              />
                              <Typography
                                variant={'bodyMedium'}
                                color={'white'}
                                className={'mix-blend'}
                                fontSize={{ xs: '12px', sm: '16px' }}>
                                {item.nftType ?? 'ERC-1155'} NFT
                              </Typography>
                            </Stack>
                            <Stack gap={{ xs: 2, sm: 3 }}>
                              <Stack
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                direction={'row'}
                                gap={{ xs: 2, sm: 3 }}>
                                <Typography
                                  variant={'displaySmall'}
                                  color={'white'}
                                  fontSize={{ xs: '20px', sm: '36px' }}>
                                  {item.name}
                                </Typography>
                                <Stack width={140} display={{ xs: 'none', sm: 'block' }}>
                                  <HexagonalButton
                                    variant={'danger'}
                                    size={'small'}
                                    label={`TIER ${item.currentTier}`}
                                  />
                                </Stack>
                                <Stack width={84} display={{ xs: 'block', sm: 'none' }}>
                                  <HexagonalButton
                                    variant={'danger'}
                                    size={'tiny'}
                                    label={`TIER ${item.currentTier}`}
                                  />
                                </Stack>
                              </Stack>
                              <Typography
                                fontSize={{ xs: '14px', md: '18px' }}
                                variant={'bodyLarge'}
                                color={'#FFFFFF'}
                                className={'mix-blend text-overflow'}
                                sx={{
                                  WebkitLineClamp: { xs: 7, sm: 6 },
                                }}>
                                {item.description}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                      <Stack>
                        <Stack px={{ xs: 3, sm: 5 }} sx={{ width: '100%' }} justifyContent={'center'}>
                          <Image
                            src={dividers.divider_1}
                            alt={'divider'}
                            width={544}
                            height={2}
                            style={{ width: '100%', height: 'auto' }}
                          />
                        </Stack>
                        <Stack padding={{ xs: '24px', sm: '32px 40px' }} minHeight={{ xs: 72, sm: 116 }}>
                          <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} gap={2}>
                            <Stack alignItems={'center'} direction={'row'} gap={{ xs: '12px', sm: 2 }}>
                              <Stack
                                alignItems={'center'}
                                justifyContent={'center'}
                                display={{ xs: 'none', sm: 'flex' }}>
                                <CoinIcon tokenImage={item.tokenImage} size={52} />
                              </Stack>
                              <Stack
                                alignItems={'center'}
                                justifyContent={'center'}
                                display={{ xs: 'flex', sm: 'none' }}>
                                <CoinIcon tokenImage={item.tokenImage} size={24} />
                              </Stack>
                              <Typography
                                variant={'displaySmall'}
                                fontSize={{ xs: '20px', sm: '36px' }}
                                lineHeight={{ xs: '24px', sm: '48px' }}
                                color={'white'}>
                                {formatReadableNumber(item.price, {
                                  minimumFractionDigits: 2,
                                  style: 'decimal',
                                  preventRounding: true,
                                })}{' '}
                                {(item.token || 'USDC').toUpperCase()}
                              </Typography>
                            </Stack>
                            <Stack width={84} display={{ xs: 'none', sm: 'block' }}>
                              <HexagonalButton
                                variant={'secondary'}
                                size={'small'}
                                onClick={() => openNodeSaleUrl(item.nodeSaleUrl)}>
                                <Image width={24} height={24} src={icons.ic_arrow_right} alt={''} />
                              </HexagonalButton>
                            </Stack>
                            <Stack width={68} display={{ xs: 'block', sm: 'none' }}>
                              <HexagonalButton
                                variant={'secondary'}
                                size={'small'}
                                onClick={() => openNodeSaleUrl(item.nodeSaleUrl)}>
                                <Image width={18} height={18} src={icons.ic_arrow_right} alt={''} />
                              </HexagonalButton>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack
                      width={{ xs: 10, sm: 17 }}
                      height={{ xs: 52, sm: 87 }}
                      position={'absolute'}
                      bottom={2}
                      left={0}
                      zIndex={2}>
                      <Image
                        src={highlights.vertical_highlight}
                        alt={'highlight'}
                        width={17}
                        height={87}
                        style={{ width: '100%', height: 'auto' }}
                      />
                    </Stack>
                  </NodeCard>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        )}
        {!isLoadingNodes && nodes?.meta && nodes?.meta.total > 0 && (
          <Pagination meta={nodes?.meta} onPageChange={handlePageChange} />
        )}
      </Stack>
    </Stack>
  );
};
